<template>
  <div>
    <h2 class="intro-y text-lg font-medium mt-10">Participants</h2>
    <p class="mt-3 text-gray-600">
      Event year: {{ eventYear || "Not configured" }}. Search covers all
      participants registered for this year.
    </p>
    <div class="flex flex-wrap items-center justify-between gap-3 mt-5">
      <label>
        <span class="sr-only">Search current-year participants</span>
        <input
          v-model="search"
          type="search"
          maxlength="200"
          class="form-control w-80"
          placeholder="Search all participants…"
        />
      </label>
      <span>{{ total }} matching participants</span>
    </div>
    <p v-if="error" role="alert" class="mt-4 text-red">
      {{ error }}
      <button
        type="button"
        class="btn btn-outline-secondary ml-3"
        :disabled="loading"
        @click="refresh"
      >
        Try again
      </button>
    </p>
    <p v-if="loading" role="status" class="mt-4">Loading participants…</p>
    <p v-else-if="!error && !total" role="status" class="mt-4">
      No matching participants for {{ eventYear }}.
    </p>
    <div class="intro-y mt-5" :aria-busy="loading">
      <ParticipantGrid
        v-if="!loading && !error"
        :grid-data="formattedParticipants"
        server-side
        :sort="sort"
        @sort="changeSort"
      />
    </div>
    <div
      v-if="totalPages > 1"
      class="mt-5 flex gap-3 justify-center items-center"
    >
      <button
        type="button"
        class="btn btn-outline-secondary"
        :disabled="loading || page <= 1"
        @click="changePage(page - 1)"
      >
        Previous
      </button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button
        type="button"
        class="btn btn-outline-secondary"
        :disabled="loading || page >= totalPages"
        @click="changePage(page + 1)"
      >
        Next
      </button>
    </div>
  </div>
</template>
<script>
import { defineComponent, computed } from "vue";
import { useStore } from "@/store";
import { getParticipants } from "@/api/participants.js";
import ParticipantGrid from "@/components/back-end/ParticipantsGrid";
import { usePagedSearch } from "@/composables/use-paged-search";

export default defineComponent({
  components: { ParticipantGrid },
  setup() {
    const store = useStore();
    const eventYear = computed(() => {
      const settings = store.getters["main/eventSettings"];
      return settings && settings.appYear;
    });
    const list = usePagedSearch(options => {
      if (!/^\d{4}$/.test(eventYear.value || ""))
        throw new Error("Save a valid event year before viewing participants.");
      return getParticipants(eventYear.value, options);
    }, eventYear);
    const formattedParticipants = computed(() =>
      list.data.value.map(p => ({
        ...p,
        name: `${p.firstName} ${p.lastName}`,
        hasTeam: !!(p.teams && p.teams.length)
      }))
    );
    return { ...list, eventYear, formattedParticipants };
  }
});
</script>
